import { useCallback, useEffect, useRef, useState } from "react";
import { toastErro, toastSucesso } from "components/Toast";
import { AxiosRequest } from "_services/api";

const api = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);
const apiPlataforma = AxiosRequest.makeApi();

export const CHAVE_DESPACHANTE_CODIGO = "@despachante_codigo_dois_fatores";

const PERIODO_PADRAO = 30;
export const SEGUNDOS_MINIMOS = 10;
const URL_TELA_USUARIO_DETRAN = "pages/veiculo/Usuario_Detran.php";
const PERMISSAO_CODIGO_DETRAN = "obter_codigo_detran";

export interface IDespachanteCodigo {
  id: number;
  despachante: string;
}

interface IUsuarioDetran {
  id: number;
  despachante: string;
  possui_chave_validacao: number;
}

export function useCodigoDetran(aberto: boolean) {
  const [liberado, setLiberado] = useState<boolean>(false);
  const [despachantes, setDespachantes] = useState<IDespachanteCodigo[]>([]);
  const [idDespachante, setIdDespachante] = useState<number | null>(null);
  const [codigo, setCodigo] = useState<string>("");
  const [segundosRestantes, setSegundosRestantes] = useState<number>(0);
  const [periodoSegundos, setPeriodoSegundos] = useState<number>(PERIODO_PADRAO);
  const [carregando, setCarregando] = useState<boolean>(false);
  const [carregandoDespachantes, setCarregandoDespachantes] = useState<boolean>(false);
  const [aguardandoNovoCiclo, setAguardandoNovoCiclo] = useState<boolean>(false);
  const [permitirNovaTentativa, setPermitirNovaTentativa] = useState<boolean>(false);
  const [erro, setErro] = useState<string>("");

  const requisicaoAtual = useRef<number>(0);
  const segundosRestantesAtual = useRef<number>(0);

  useEffect(() => {
    segundosRestantesAtual.current = segundosRestantes;
  }, [segundosRestantes]);

  useEffect(() => {
    async function verificarPermissao() {
      const { data } = await apiPlataforma.get(
        `/autenticacao/subfuncionalidades?url=${URL_TELA_USUARIO_DETRAN}`
      );

      return (
        Array.isArray(data) &&
        data.some((item) => item.nome_funcionalidade === PERMISSAO_CODIGO_DETRAN)
      );
    }

    async function listarDespachantes() {
      const { data } = await apiPlataforma.get("/orgao/");
      const usuarios: IUsuarioDetran[] = Array.isArray(data) ? data : [];

      return usuarios
        .filter((item) => Number(item.possui_chave_validacao) === 1)
        .map(({ id, despachante }) => ({ id, despachante }))
        .sort((atual, proximo) => atual.despachante.localeCompare(proximo.despachante));
    }

    async function iniciar() {
      try {
        if (!(await verificarPermissao())) {
          return;
        }

        setLiberado(true);
        setCarregandoDespachantes(true);

        const lista = await listarDespachantes();

        setDespachantes(lista);
        setIdDespachante(recuperarDespachanteSalvo(lista));
      } catch (error: any) {
        const status = error?.response?.status;

        if (status !== 401 && status !== 403 && status !== 404) {
          console.error(
            "Código de dois fatores indisponível na navbar:",
            error?.message ?? error
          );
        }
      } finally {
        setCarregandoDespachantes(false);
      }
    }

    iniciar();
  }, []);

  const gerarCodigo = useCallback(async (id: number, automatico = false) => {
    requisicaoAtual.current += 1;
    const requisicao = requisicaoAtual.current;

    setAguardandoNovoCiclo(
      segundosRestantesAtual.current > 0 && segundosRestantesAtual.current <= SEGUNDOS_MINIMOS
    );
    setCarregando(true);
    setErro("");
    setPermitirNovaTentativa(false);
    setSegundosRestantes(0);

    // Na renovação automática o código anterior fica visível, esmaecido,
    // enquanto o novo não chega — evita piscar a cada ciclo.
    if (!automatico) {
      setCodigo("");
    }

    const inicio = performance.now();

    try {
      const { data } = await api.get(`/detran-pr/validacao/despachante/${id}`);

      if (requisicao !== requisicaoAtual.current) {
        return;
      }

      const duracaoSegundos = (performance.now() - inicio) / 1000;
      const latenciaSegundos = Math.max(0, duracaoSegundos - (data.segundosAguardados || 0));

      setCodigo(data.codigo);
      setPeriodoSegundos(data.periodoSegundos || PERIODO_PADRAO);
      setSegundosRestantes(Math.max(0, data.segundosRestantes - latenciaSegundos));
    } catch (error: any) {
      if (requisicao !== requisicaoAtual.current) {
        return;
      }

      setCodigo("");
      setErro(error?.response?.data?.message ?? "Não foi possível gerar o código");
      setPermitirNovaTentativa(true);
    } finally {
      if (requisicao === requisicaoAtual.current) {
        setCarregando(false);
        setAguardandoNovoCiclo(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!aberto || !idDespachante) {
      requisicaoAtual.current += 1;
      setCodigo("");
      setSegundosRestantes(0);
      setErro("");
      setCarregando(false);
      return;
    }

    gerarCodigo(idDespachante);
  }, [aberto, idDespachante, gerarCodigo]);

  useEffect(() => {
    if (segundosRestantes <= 0) {
      return;
    }

    const temporizador = setTimeout(() => {
      setSegundosRestantes((valor) => Math.max(0, valor - 1));
    }, 1000);

    return () => clearTimeout(temporizador);
  }, [segundosRestantes]);

  useEffect(() => {
    if (!aberto || !idDespachante || carregando || permitirNovaTentativa) {
      return;
    }

    if (!codigo || segundosRestantes > 0) {
      return;
    }

    gerarCodigo(idDespachante, true);
  }, [
    aberto,
    idDespachante,
    codigo,
    segundosRestantes,
    carregando,
    permitirNovaTentativa,
    gerarCodigo,
  ]);

  const selecionarDespachante = useCallback((id: number) => {
    if (!id) {
      localStorage.removeItem(CHAVE_DESPACHANTE_CODIGO);
      setIdDespachante(null);
      return;
    }

    localStorage.setItem(CHAVE_DESPACHANTE_CODIGO, String(id));
    setIdDespachante(id);
  }, []);

  const tentarNovamente = useCallback(() => {
    if (!idDespachante) {
      return;
    }

    gerarCodigo(idDespachante);
  }, [idDespachante, gerarCodigo]);

  const copiarCodigo = useCallback(() => {
    if (!codigo) {
      return;
    }

    if (!navigator.clipboard) {
      toastErro("Funcionalidade indisponível devido a configurações do navegador");
      return;
    }

    navigator.clipboard
      .writeText(codigo)
      .then(() => toastSucesso("Código copiado!"))
      .catch(() => toastErro("Falha ao copiar"));
  }, [codigo]);

  return {
    liberado,
    carregandoDespachantes,
    aguardandoNovoCiclo,
    permitirNovaTentativa,
    despachantes,
    idDespachante,
    selecionarDespachante,
    codigo,
    segundosRestantes,
    periodoSegundos,
    expirado: !!codigo && segundosRestantes <= 0,
    carregando,
    erro,
    tentarNovamente,
    copiarCodigo,
  };
}

function recuperarDespachanteSalvo(lista: IDespachanteCodigo[]): number | null {
  const salvo = Number(localStorage.getItem(CHAVE_DESPACHANTE_CODIGO));

  if (!salvo || !lista.some((item) => Number(item.id) === salvo)) {
    return null;
  }

  return salvo;
}
